/**
 * lib/financeAudit.ts
 * ------------------------------------------------------------------
 * One audit entry per sensitive finance action — mirrors lib/hrAudit.ts.
 * Never throws (logs to console on failure) so a write is never lost to
 * an audit hiccup.
 * ------------------------------------------------------------------ */
import { finance } from '@/features/finance/financeWrite';
import { logAudit } from '@/lib/audit';
import { stripUndefined } from '@/lib/sanitize';

export interface FinanceAuditActor {
  id: string;
  email?: string | null;
}

export interface RecordFinanceAuditInput {
  /** Verb phrase, e.g. 'billing.run', 'payment.capture', 'invoice.void'. */
  action: string;
  actor: FinanceAuditActor | null;
  /** Subject reference: an invoiceId / paymentId / studentId / familyId. */
  subjectId?: string;
  before?: unknown;
  after?: unknown;
}

export async function recordFinanceAudit(input: RecordFinanceAuditInput): Promise<void> {
  try {
    await finance.auditLog.add({
      action: input.action,
      actorId: input.actor?.id ?? 'unknown',
      actorEmail: input.actor?.email ?? undefined,
      subjectId: input.subjectId,
      before: input.before ?? undefined,
      after: input.after ?? undefined,
      at: new Date().toISOString(),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('financeAudit: failed to write audit entry', input.action, err);
  }

  // Mirror to the central activity log (category 'finance') so finance
  // actions surface in the Activity Log screen. Independent of the silo
  // write above (financeAuditLog stays the immutable finance record).
  const details = stripUndefined({ before: input.before, after: input.after });
  const entry = {
    action: input.action,
    category: 'finance' as const,
    actorId: input.actor?.id ?? 'unknown',
    ...(input.actor?.email ? { actorEmail: input.actor.email } : {}),
    ...(input.subjectId ? { targetId: input.subjectId } : {}),
    ...(Object.keys(details).length ? { details } : {}),
  };
  void logAudit(entry);
}
